import type { WidgetDefinition } from '../global'

// `@property` registrations are document-global (a shadow root's adopted
// stylesheet ignores them), so they have to be adopted on `document` — but only
// once. Track which rules are already registered so multiple widget instances
// (or widget types) don't stack duplicate global stylesheets on every mount.
const registeredPropertyRules = new Set<string>()

export class HolidayguruWidget extends HTMLElement {
  root: ShadowRoot | null = null
  definition: WidgetDefinition | null = null
  private reactRoot: any = null
  private component: any = null
  static observedAttributes: string[] = [
    'variant',
    'partner',
    'linkinlogo',
    'phonenr',
    'servicehours',
    'logosrc',
    'logoalt',
    'logowidth',
    'logoheight',
    'contentwidth',
    'traveler',
    'travelers',
    'unavailablereason',
    'selected',
  ]

  constructor() {
    super()
  }

  createCSS() {
    if (!this.definition?.css) return

    const propertyRules = new Set<string>()

    this.definition.css.forEach(({ css, target }) => {
      const styles = new CSSStyleSheet()
      styles.replaceSync(css)
      const root = target === 'shadow' ? this.root : document
      root?.adoptedStyleSheets.push(styles)

      // `@property` at-rules only register at the document level — a shadow
      // root's adopted stylesheet ignores them. That silently breaks animated
      // registered custom properties inside the shadow tree (e.g. the
      // assistant orb's conic-gradient `--aui-orb-angle`, which then jumps
      // between keyframes and renders invalid instead of rotating smoothly).
      // Collect them and register them on the document below; registration is
      // document-wide and applies inside shadow roots too.
      if (target === 'shadow') {
        const matches = css.match(/@property\s+--[\w-]+\s*\{[^}]*\}/g)
        matches?.forEach((rule) => propertyRules.add(rule))
      }
    })

    const newRules = [...propertyRules].filter(
      (rule) => !registeredPropertyRules.has(rule),
    )
    if (newRules.length > 0) {
      const propertySheet = new CSSStyleSheet()
      propertySheet.replaceSync(newRules.join('\n'))
      document.adoptedStyleSheets.push(propertySheet)
      newRules.forEach((rule) => registeredPropertyRules.add(rule))
    }
  }

  renderComponent() {
    if (!this.reactRoot || !this.component) return

    import('react').then(({ createElement }) => {
      const attrs = {} as Record<string, string | null>

      if (this.definition?.attributes) {
        Object.entries(this.definition.attributes).forEach(([key, value]) => {
          attrs[key] = this.getAttribute(key) ?? value
        })
      }

      this.reactRoot.render(createElement(this.component, attrs))
    })
  }

  async connectedCallback() {
    if (!this.definition) {
      throw new Error('Widget definition not set!')
    }
    this.root = this.attachShadow({ mode: 'open' })

    // React clears only an Element or Document container when a failed hydration falls
    // back to a client render; hydrated straight into the ShadowRoot, the mismatched
    // prerender would stay next to the client output.
    const container = document.createElement('div')
    container.style.display = 'contents'
    this.root.append(container)

    // Pre-render only if there are no attributes
    if (this.definition.attributes == null) {
      container.innerHTML = this.definition.html
    }

    this.createCSS()

    Promise.all([
      import('react'),
      import('react-dom/client'),
      import(this.definition.entry),
    ]).then(
      ([
        { createElement },
        { createRoot, hydrateRoot },
        { default: component },
      ]) => {
        this.component = component

        if (this.definition?.attributes) {
          this.reactRoot = createRoot(container)
          this.renderComponent()
        } else {
          hydrateRoot(container, createElement(component))
        }
      },
    )
  }

  attributeChangedCallback() {
    this.renderComponent()
  }
}
